/**
 * Email-gated discount code with a scratch reveal.
 *
 * The code lives on a data attribute and is only written into the panel after a
 * signup. That hides it from the page, not from dev tools - a theme has no
 * server to fetch it from, so it has to be in the document somewhere. This is a
 * conversion device rather than an access control; see the snippet's header for
 * what to do if the code genuinely must not leak.
 *
 * Scratching is a canvas with globalCompositeOperation = destination-out: the
 * covering is painted once, and the pointer erases holes in it. Erased area is
 * sampled rather than guessed, so "mostly cleared" means measured pixels.
 *
 * Touch is the primary input. touch-action: none is set ONLY while a pointer is
 * actually down on the panel, so the page scrolls normally the rest of the
 * time - a scratch panel that eats vertical scroll is worse than no scratch
 * panel.
 *
 * Reduced motion skips the scratch entirely and fades the code in.
 */
(function () {
  /* Editable wording. Sources: Theme settings > "Text: ..." groups,
     serialised by snippets/calmy-strings.liquid. The second argument is
     the wording this file shipped with and is used when the snippet is
     absent or the setting is blank. */
  var CT = function (k, f) { return window.CalmyT ? window.CalmyT(k, f) : f; };

  "use strict";

  var still = window.matchMedia("(prefers-reduced-motion: reduce)");

  function init(root) {
    if (root.__calmyGate) return;
    root.__calmyGate = true;

    /* The reveal threshold is a per-block setting, clamped so a typo cannot
       make the panel impossible to clear. Read here, inside init, because it
       is read off the element — at module scope there is no element yet. */
    var REVEAL_AT = (function () {
      var v = parseFloat(root.getAttribute("data-threshold"));
      if (isNaN(v)) return 0.45;
      return Math.min(0.95, Math.max(0.05, v > 1 ? v / 100 : v));
    })();

    var code = root.getAttribute("data-code") || "";
    var key = root.getAttribute("data-key") || "calmy-gate";
    var formBox = root.querySelector("[data-calmy-gate-form]");
    var won = root.querySelector("[data-calmy-gate-won]");
    var panel = root.querySelector("[data-calmy-gate-panel]");
    var canvas = root.querySelector("[data-calmy-gate-canvas]");
    var codeEl = root.querySelector("[data-calmy-gate-code]");
    var hint = root.querySelector("[data-calmy-gate-hint]");
    var skip = root.querySelector("[data-calmy-gate-skip]");
    var copy = root.querySelector("[data-calmy-gate-copy]");
    var copyText = root.querySelector("[data-calmy-gate-copytext]");
    var email = root.querySelector("[data-calmy-gate-email]");
    var err = root.querySelector("[data-calmy-gate-err]");
    var form = root.querySelector("form");
    if (!formBox || !won || !panel) return;

    var revealed = false;

    function remember(state) {
      try { localStorage.setItem(key, state); } catch (e) {}
    }
    function recall() {
      try { return localStorage.getItem(key); } catch (e) { return null; }
    }

    /* ---- state 3 ---- */
    function reveal(instant) {
      if (revealed) return;
      revealed = true;
      codeEl.textContent = code;
      panel.classList.add("is-revealed");
      if (hint) hint.hidden = true;
      if (skip) skip.hidden = true;
      if (canvas) canvas.classList.add("is-gone");
      if (copy) copy.hidden = false;
      var terms = root.querySelector("[data-calmy-gate-terms]");
      if (terms) terms.hidden = false;
      if (copyText) copyText.textContent = copy.getAttribute('data-idle') || 'Copy';
      remember("revealed");
      sync(key, "revealed", null);
      if (instant && canvas) canvas.style.transition = "none";
    }

    /* The canvas is sized from the panel's rect, so a width change leaves it
       stretched and the erased area no longer lines up with the finger.
       Repainting resets the cover — which is correct: a resize mid-scratch is
       a new surface, and carrying erased pixels across at the wrong scale
       would show the code through gaps that were never scratched. */
    var resizeTimer = 0;
    window.addEventListener("resize", function () {
      if (revealed || !canvas || won.hidden) return;
      window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(function () {
        if (revealed || won.hidden) return;
        lastPt = null;
        paint();
      }, 120);
    }, { passive: true });

    /* ---- state 2 ---- */
    function toScratch(instant) {
      formBox.hidden = true;
      won.hidden = false;
      codeEl.textContent = code;
      remember("won");

      if (still.matches) { reveal(true); return; }
      if (!canvas || !canvas.getContext) { reveal(true); return; }
      paint();
    }

    function paint() {
      var rect = panel.getBoundingClientRect();
      var dpr = Math.min(window.devicePixelRatio || 1, 2);
      var w = Math.max(1, Math.round(rect.width));
      var h = Math.max(1, Math.round(rect.height));
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      canvas.style.width = w + "px";
      canvas.style.height = h + "px";

      var ctx = canvas.getContext("2d");
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      // The covering: the surface tone, so it reads as an unopened panel.
      /* The overlay tone is a setting, falling back to the theme's hairline.
         Read from the element rather than hardcoded so a merchant can retune
         it without touching CSS. */
      var css = getComputedStyle(document.documentElement);
      var base = (root.getAttribute("data-scratch") || "").trim()
        || (css.getPropertyValue("--c-line") || "#DBD1C0").trim();
      ctx.fillStyle = base;
      ctx.fillRect(0, 0, w, h);

      // A little grain, so it looks like a covering rather than a rectangle.
      ctx.globalAlpha = 0.16;
      for (var i = 0; i < (w * h) / 420; i++) {
        ctx.fillStyle = i % 2 ? "#ffffff" : "#000000";
        ctx.fillRect(Math.random() * w, Math.random() * h, 1.5, 1.5);
      }
      ctx.globalAlpha = 1;
      ctx.globalCompositeOperation = "destination-out";
      canvas.__ctx = ctx;
      canvas.__w = w;
      canvas.__h = h;
    }

    var BRUSH = 22;
    var lastPt = null;

    function dot(ctx, x, y) {
      ctx.beginPath();
      ctx.arc(x, y, BRUSH, 0, Math.PI * 2);
      ctx.fill();
    }

    /* INTERPOLATED, not a dot per event.

       pointermove fires at the pointer's sampling rate, not at the speed of the
       hand: a fast drag across a 300px panel can deliver two events 200px
       apart, and stamping one circle at each left a dashed trail with unerased
       gaps between them. Every segment is walked in steps of half the brush
       radius, so consecutive stamps always overlap and the stroke is solid at
       any speed. */
    function erase(x, y) {
      var ctx = canvas.__ctx;
      if (!ctx) return;
      if (!lastPt) { dot(ctx, x, y); lastPt = { x: x, y: y }; return; }
      var dx = x - lastPt.x, dy = y - lastPt.y;
      var dist = Math.sqrt(dx * dx + dy * dy);
      var step = BRUSH / 2;
      var n = Math.max(1, Math.ceil(dist / step));
      for (var i = 1; i <= n; i++) {
        dot(ctx, lastPt.x + (dx * i) / n, lastPt.y + (dy * i) / n);
      }
      lastPt = { x: x, y: y };
    }

    /* Sampled on a coarse grid: reading every pixel of a 300x60 canvas on every
       move is wasted work when a 1-in-16 sample answers the same question. */
    function cleared() {
      var ctx = canvas.__ctx;
      if (!ctx) return 0;
      var d;
      try { d = ctx.getImageData(0, 0, canvas.width, canvas.height).data; }
      catch (e) { return 0; }
      var gone = 0, seen = 0;
      for (var i = 3; i < d.length; i += 4 * 16) { seen++; if (d[i] < 40) gone++; }
      return seen ? gone / seen : 0;
    }

    var drawing = false;
    var sampleTimer = 0;

    function pointFrom(e) {
      var r = canvas.getBoundingClientRect();
      return { x: e.clientX - r.left, y: e.clientY - r.top };
    }

    /* Sampled on a 150ms interval rather than after every move.

       getImageData is a readback from the GPU and stalls the pipeline; running
       it per move event ties the cost of the check to how fast somebody drags,
       which is exactly backwards. A fixed cadence bounds the work regardless,
       and 150ms is far below the time it takes to scratch another few percent. */
    function startSampling() {
      if (sampleTimer) return;
      sampleTimer = window.setInterval(function () {
        if (revealed) { stopSampling(); return; }
        if (cleared() >= REVEAL_AT) { stopSampling(); reveal(false); }
      }, 150);
    }
    function stopSampling() {
      if (!sampleTimer) return;
      window.clearInterval(sampleTimer);
      sampleTimer = 0;
    }
    function check() { if (!revealed && cleared() >= REVEAL_AT) reveal(false); }

    if (canvas) {
      canvas.addEventListener("pointerdown", function (e) {
        if (revealed) return;
        drawing = true;
        /* touch-action: none lives in the stylesheet, not here. Setting it on
           pointerdown is already too late: the browser decides whether a touch
           is a scroll from the first move, and by then the gesture has been
           claimed and the page has moved under the finger. */
        canvas.setPointerCapture(e.pointerId);
        lastPt = null;
        var p = pointFrom(e);
        erase(p.x, p.y);
        if (hint) hint.hidden = true;
        startSampling();
      });

      canvas.addEventListener("pointermove", function (e) {
        if (!drawing || revealed) return;
        var p = pointFrom(e);
        erase(p.x, p.y);
      });

      function end(e) {
        if (!drawing) return;
        drawing = false;
        lastPt = null;
        stopSampling();
        if (e && e.pointerId != null && canvas.hasPointerCapture(e.pointerId)) {
          canvas.releasePointerCapture(e.pointerId);
        }
        check();
      }
      canvas.addEventListener("pointerup", end);
      canvas.addEventListener("pointercancel", end);
      canvas.addEventListener("pointerleave", end);
    }

    if (skip) skip.addEventListener("click", function () { reveal(false); });

    if (copy) {
      copy.addEventListener("click", function () {
        var done = root.getAttribute("data-copied") || "Copied";
        function ok() {
          if (!copyText) return;
          copyText.textContent = done;
          window.setTimeout(function () {
            copyText.textContent = copy.getAttribute("data-idle") || "Copy";
          }, 1800);
        }
        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(code).then(ok, function () {});
        } else { ok(); }
      });
    }

    /* ---- submit ---- */
    if (form) {
      form.addEventListener("submit", function (e) {
        var value = (email && email.value || "").trim();
        // Inline validation: never a silent failure and never a page reload
        // that loses what was typed.
        if (!value || !/^[^@\s]+@[^@\s]+\.[^@\s]{2,}$/.test(value)) {
          e.preventDefault();
          if (err) { err.textContent = CT("gate_email_invalid", "Enter a valid email address."); err.hidden = false; }
          if (email) email.focus();
          return;
        }
        if (err) err.hidden = true;

        if (!window.fetch) return; // let it post normally
        e.preventDefault();

        var btn = root.querySelector("[data-calmy-gate-submit]");
        var golabel = root.querySelector("[data-calmy-gate-golabel]");
        var spin = root.querySelector("[data-calmy-gate-spin]");

        /* THE BUTTON IS THE ONLY THING THAT MOVES.

           Its width is pinned to the width it currently has before the label is
           swapped for the spinner. Without that the button shrinks to the
           spinner's width, the row reflows, and on mobile — where the button is
           full width under the input — the whole block changes height. The
           point of the loading state is to show that something is happening,
           not to rearrange the page while it does. */
        if (btn) {
          btn.style.width = Math.ceil(btn.getBoundingClientRect().width) + "px";
          btn.disabled = true;
          btn.setAttribute("aria-busy", "true");
        }
        if (golabel) golabel.hidden = true;
        if (spin) spin.hidden = false;

        function restore() {
          if (spin) spin.hidden = true;
          if (golabel) golabel.hidden = false;
          if (btn) {
            btn.disabled = false;
            btn.removeAttribute("aria-busy");
            btn.style.width = "";
          }
        }

        /* Posted as a form body to Shopify's own customer endpoint, so the
           record is created exactly as the native form would create it. */
        var data = new FormData(form);
        fetch(form.action, { method: "POST", body: data, headers: { Accept: "text/html" }, redirect: "follow" })
          .then(function (res) {
            restore();
            /* THE SUCCESS SIGNAL IS SHOPIFY'S, NOT OURS.

               This used to advance to the coupon whenever the request merely
               COMPLETED, which handed a discount to any syntactically valid
               address — including ones the platform rejected. It is the
               platform that decides whether a customer record was created,
               and it says so in one place: on success the customer endpoint
               redirects back with customer_posted=true in the query string.
               `redirect: "follow"` means res.url is that final URL.

               No flag, no code. An address Shopify did not accept does not
               get past this line. */
            if (accepted(res.url)) { markSubscribed(key); toScratch(false); return; }

            /* Shopify re-rendered the page instead of redirecting, which is
               what it does when the address is refused. We cannot read its
               field-level message from here — the response is the whole page
               and our error node is client-side only — so this says what is
               true and keeps what was typed. */
            if (err) {
              err.textContent = CT("gate_rejected", "That address was not accepted. Check it and try again.");
              err.hidden = false;
            }
            if (email) { email.focus(); email.select && email.select(); }
          })
          .catch(function () {
            /* A rejected fetch is a request that never completed — offline, DNS,
               a dropped connection. Advancing to the code on that would hand out
               a discount for an address that was never recorded, so it says so
               and leaves the typed address where it is. */
            restore();
            if (err) {
              err.textContent = CT("gate_send_failed", "That did not send. Check your connection and try again.");
              err.hidden = false;
            }
            if (email) email.focus();
          });
      });
    }

    /* Returning visitors skip straight to where they left off. */
    function apply(state) {
      if (state === "revealed") { formBox.hidden = true; won.hidden = false; reveal(true); }
      else if (state === "won" && won.hidden) { toScratch(true); }
    }
    live.push({ key: key, apply: apply, root: root });
    apply(recall());

    window.addEventListener("resize", function () {
      if (!revealed && !won.hidden && canvas && canvas.__ctx) paint();
    }, { passive: true });
  }

  /* ── WHAT WE ARE ALLOWED TO REMEMBER ─────────────────────────────────────
     A boolean per discount code, and nothing else. No address is written to
     storage: an email is personal data, this is a shopper's own device, and
     nothing on the page needs it after the POST.

     And this marker is NOT proof of subscription. It is a record that THIS
     browser completed a signup that Shopify accepted — it does not travel to
     another device, it can be cleared, and it is not consulted by anything
     that grants an actual discount. The discount is enforced at checkout by
     the Shopify code, which is where eligibility genuinely lives. All this
     does is avoid asking the same person for their address twice in a row. */
  function accepted(url) {
    return /[?&]customer_posted=true/.test(String(url || ""));
  }
  function markSubscribed(key) {
    try { localStorage.setItem(key, "won"); } catch (e) {}
    sync(key, "won");
  }
  /* Every gate on the page sharing a key moves together, so the footer strip
     and the product strip cannot disagree during one visit. */
  var live = [];
  function sync(key, state, from) {
    live.forEach(function (g) { if (g.key === key && g !== from) g.apply(state); });
  }
  /* Another tab of the same site. Same signal, same rule. */
  window.addEventListener("storage", function (e) {
    if (!e.key || e.newValue == null) return;
    if (e.key.indexOf("calmy-gate-") !== 0) return;
    sync(e.key, e.newValue);
  });

  function boot(root) {
    (root || document).querySelectorAll("[data-calmy-gate]").forEach(init);
    /* A FULL-PAGE signup — the footer's form is a native Shopify POST with no
       script of its own — lands back here with customer_posted=true. That is
       the same server-confirmed signal the AJAX path reads, so a person who
       subscribed in the footer meets the scratch panel on the product page
       rather than being asked for their address a second time. */
    if (accepted(location.search)) {
      live.forEach(function (g) {
        var seen = null;
        try { seen = localStorage.getItem(g.key); } catch (e) {}
        if (seen !== "revealed") markSubscribed(g.key);
      });
    }
  }
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", function () { boot(); });
  else boot();
  document.addEventListener("shopify:section:load", function (e) { boot(e.target); });
})();
